import {
  schAlert,
  schAloftDate,
  schAloftPrice,
  schPropertyInterest,
  schZoningCompliance,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

// Validation schema for Appraisify validation
// This schema is used purely for validation purposes, not for UI forms
export const schAppraisifyValidation = z.object({
  // Alert validation
  alerts: z.object({
    alerts: z.array(schAlert).refine((alerts) => alerts.every((a) => a.status === 'dismissed'), {
      message: 'There are active alerts that need to be addressed',
    }),
  }),

  // Order Details validation
  orderDetails: z
    .object({
      zoning: z.string().min(1, 'Required'),
      zoningCompliance: schZoningCompliance,
      parcelNumber: z.string().min(1, 'Required'),
      legalDescription: z.string().min(1, 'Required'),
      effectiveDate: schAloftDate,
      intendedUsers: z.string().min(1, 'Required'),
      intendedUse: z.string().min(1, 'Required'),
      propertyInterest: schPropertyInterest,
      propertyInterestOther: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.propertyInterest === 'Other') {
        if (!data.propertyInterestOther || data.propertyInterestOther.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['propertyInterestOther'],
          });
        }
      }
    }),

  // Analysis validation
  analysis: z.object({
    comps: z.array(z.string()).min(1, 'At least one comparable is required'),
    opinionOfSiteValue: schAloftPrice.refine(
      (price) => price.toDollarLike() > 0,
      'Opinion of site value must be greater than 0',
    ),
  }),

  // Market validation
  market: z.object({}),

  // Commentary validation
  commentary: z.object({
    estimatedDemand: z.string().min(1, 'Required'),
    estimatedExposureTime: z.string().min(1, 'Required'),
    marketConditionCommentary: z.string().min(1, 'Required'),
    priorSalesCommentary: z.string().min(1, 'Required'),
  }),

  // Reconciliation validation
  reconciliation: z.object({
    totalCostToCure: schAloftPrice,
    opinionOfMarketValue: schAloftPrice.refine((price) => price.toDollarLike() > 0, 'Required'),
    reconciliationCommentary: z.string().min(1, 'Required'),
    highestAndBestUseCommentary: z.string().min(1, 'Required'),
    revisionCommentary: z.string().min(1, 'Required'),
    alternativeComparablesCommentary: z.array(
      z.string().min(1, 'Each alternative comparable must be addressed with a commentary'),
    ),
  }),
});

export type AppraisifyValidationData = z.infer<typeof schAppraisifyValidation>;
